'use no memo';
/**
 * Native Logto Adapter Provider
 *
 * Wraps @logto/capacitor's CapacitorLogtoClient in a React context that exposes
 * the same LogtoAdapterValue interface as WebLogtoAdapterProvider.
 *
 * Key differences from web:
 * - signIn() opens system browser (ASWebAuthenticationSession/Chrome Custom Tabs)
 *   and resolves when user completes or cancels auth
 * - Token persistence uses @capacitor/preferences (survives app restarts)
 * - getAccessToken() auto-refreshes via stored refresh token (offline_access always included)
 * - Module-level singleton prevents StrictMode double-mount issues (AR-3 fix)
 *
 * AIDEV-NOTE: 'use no memo' -- This provider manages async SDK state in useState.
 * The React Compiler cannot optimize the async state sync pattern.
 */

import { useState, useEffect, useMemo, type ReactNode } from 'react';
import CapacitorLogtoClient from '@logto/capacitor';
import { UserScope } from '@logto/capacitor';
import type { LogtoConfig } from '@logto/browser';
import { LogtoAdapterContext, type LogtoAdapterValue, type LogtoSignInOptions } from './types';
import { isNativePlatform } from '@lib/platform';
import { isUserCancellation } from '@utils/auth';
import { logger } from '@utils/logger';

/**
 * Map LogtoSignInOptions.firstScreen to the interactionMode string expected
 * by @logto/capacitor's signIn(redirectUri, interactionMode) overload.
 *
 * AIDEV-NOTE: The Capacitor SDK explicitly does NOT support the SignInOptions
 * object overload (signIn(options)). We must extract redirectUri + interactionMode
 * and pass them as positional args. loginHint is injected via adapter.navigate wrapper below.
 */
function toInteractionMode(firstScreen?: string): 'signIn' | 'signUp' | undefined {
  if (firstScreen === 'register' || firstScreen === 'signUp') return 'signUp';
  if (firstScreen === 'signIn') return 'signIn';
  return undefined;
}

// ---------------------------------------------------------------------------
// Module-level singleton (AR-3 fix: survives StrictMode double-mount + HMR)
// ---------------------------------------------------------------------------

let nativeLogtoClient: CapacitorLogtoClient | null = null;

// AIDEV-NOTE: @logto/capacitor v4.0.4 doesn't forward loginHint to the base client's
// signIn(). We work around this by intercepting adapter.navigate (public mutable property)
// to inject login_hint into the OIDC authorization URL before the browser opens.
let pendingLoginHint: string | null = null;

function getNativeLogtoClient(config: LogtoConfig): CapacitorLogtoClient {
  if (!nativeLogtoClient) {
    nativeLogtoClient = new CapacitorLogtoClient(config);

    // Wrap adapter.navigate to inject login_hint into sign-in URLs.
    // originalNavigate = SDK's Browser.open wrapper (set in CapacitorLogtoClient constructor)
    const originalNavigate = nativeLogtoClient.adapter.navigate;
    nativeLogtoClient.adapter.navigate = async (url, parameters) => {
      if (pendingLoginHint && parameters.for === 'sign-in') {
        const urlObj = new URL(url);
        urlObj.searchParams.set('login_hint', pendingLoginHint);
        pendingLoginHint = null;
        return originalNavigate(urlObj.toString(), parameters);
      }
      return originalNavigate(url, parameters);
    };
  }
  return nativeLogtoClient;
}

// ---------------------------------------------------------------------------
// Config for native -- uses separate Logto "Native" app (NOT SPA)
// ---------------------------------------------------------------------------

const nativeAppId = import.meta.env.VITE_LOGTO_NATIVE_APP_ID || '';
if (!nativeAppId && isNativePlatform()) {
  logger.error(
    '[NativeLogtoAdapterProvider] VITE_LOGTO_NATIVE_APP_ID is not set. Native auth will fail.',
  );
  logger.warn(
    'Ensure .sops/.env.enc contains VITE_LOGTO_NATIVE_APP_ID and you entered nix develop.',
  );
}

const logtoEndpoint = import.meta.env.VITE_LOGTO_ENDPOINT || '';
if (!logtoEndpoint && isNativePlatform()) {
  logger.error(
    '[NativeLogtoAdapterProvider] VITE_LOGTO_ENDPOINT is not set. Using production fallback.',
  );
}

const logtoResource: string = import.meta.env.VITE_LOGTO_RESOURCE || '';
if (!logtoResource && isNativePlatform()) {
  logger.error(
    '[NativeLogtoAdapterProvider] VITE_LOGTO_RESOURCE is not set. Token requests will fail.',
  );
  logger.warn('Ensure .sops/.env.enc contains VITE_LOGTO_RESOURCE and you entered nix develop.');
}

const nativeLogtoConfig: LogtoConfig = {
  endpoint: logtoEndpoint || 'https://login.meetball.fun',
  appId: nativeAppId,
  scopes: [
    UserScope.Email,
    UserScope.Phone,
    UserScope.CustomData,
    UserScope.Identities,
    UserScope.Profile,
  ],
  resources: [logtoResource],
};

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------

export function NativeLogtoAdapterProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  const client = getNativeLogtoClient(nativeLogtoConfig);

  // Restore session from @capacitor/preferences on mount
  useEffect(() => {
    let mounted = true;

    async function restoreSession() {
      try {
        const authenticated = await client.isAuthenticated();
        if (mounted) {
          setIsAuthenticated(authenticated);
          setIsLoading(false);
        }
      } catch (err) {
        if (mounted) {
          logger.error('Failed to restore native auth session:', err);
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      }
    }

    void restoreSession();

    return () => {
      mounted = false;
    };
  }, [client]);

  const value = useMemo<LogtoAdapterValue>(
    () => ({
      isAuthenticated,
      isLoading,
      error,

      signIn: async (opts: LogtoSignInOptions) => {
        setIsLoading(true);
        setError(undefined);

        try {
          // Capacitor SDK ONLY supports signIn(redirectUri, interactionMode?) --
          // the SignInOptions overload is explicitly unsupported on Capacitor.
          const redirectUri = String(opts.redirectUri);
          const interactionMode = toInteractionMode(opts.firstScreen);

          // Set pending loginHint for the adapter.navigate wrapper to inject
          if (opts.loginHint) {
            pendingLoginHint = opts.loginHint;
          }

          await client.signIn(redirectUri, interactionMode);

          // signIn resolved -- check if now authenticated
          const authenticated = await client.isAuthenticated();
          setIsAuthenticated(authenticated);
          setIsLoading(false);
        } catch (err) {
          // Always clear pending hint on any exit from signIn to prevent stale hints
          pendingLoginHint = null;

          // AR-5 fix: Distinguish user cancellation from real errors
          if (isUserCancellation(err)) {
            // User dismissed the browser intentionally -- not an error
            logger.info('Native sign-in cancelled by user');
            setIsLoading(false);
            return;
          }

          const authError = err instanceof Error ? err : new Error('Native sign-in failed');
          setError(authError);
          setIsLoading(false);
          throw authError;
        }
      },

      signOut: async (postLogoutRedirectUri?: string) => {
        try {
          await client.signOut(postLogoutRedirectUri);
          setIsAuthenticated(false);
          setError(undefined);
        } catch (err) {
          logger.error('Native sign-out failed:', err);
          // Even on failure, clear local auth state
          setIsAuthenticated(false);
          const signOutError = err instanceof Error ? err : new Error('Native sign-out failed');
          setError(signOutError);
          throw signOutError;
        }
      },

      getAccessToken: async (resource?: string, organizationId?: string) => {
        try {
          return await client.getAccessToken(resource, organizationId);
        } catch (err) {
          logger.error('Native getAccessToken failed:', err);
          throw err;
        }
      },

      getIdTokenClaims: async () => {
        try {
          return await client.getIdTokenClaims();
        } catch (err) {
          logger.error('Native getIdTokenClaims failed:', err);
          throw err;
        }
      },

      fetchUserInfo: async () => {
        try {
          return await client.fetchUserInfo();
        } catch (err) {
          logger.error('Native fetchUserInfo failed:', err);
          throw err;
        }
      },

      clearAllTokens: async () => {
        try {
          await client.clearAllTokens();
          setIsAuthenticated(false);
        } catch (err) {
          const clearError =
            err instanceof Error ? err : new Error('Failed to clear native tokens');
          logger.error('Failed to clear native Logto tokens:', clearError);
          setError(clearError);
          setIsAuthenticated(false);
          throw clearError;
        }
      },
    }),
    [client, isAuthenticated, isLoading, error],
  );

  return <LogtoAdapterContext.Provider value={value}>{children}</LogtoAdapterContext.Provider>;
}
